import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { FormikErrors, useFormik } from 'formik'
import { produce } from 'immer'
import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Dropdown } from 'primereact/dropdown'
import { InputText } from 'primereact/inputtext'
import { classNames } from 'primereact/utils'

import { useToastContext } from '../../../../../contexts/toast'
import { useSelectFirstAndOnlyOption } from '../../../../../hooks'
import { getHandleableError } from '../../../../../utils/errors/isXiaError'
import { calculateBreakpointWidth } from '../../../../../utils/helper/calculateBreakpointWidth'
import { prepareCompletePatchDto } from '../../../../../utils/helper/prepareCompletePatchDto'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../utils/helper/validateFormikFields'
import { ConfigurableComponent } from '../../../../../utils/wrapper'
import { useConfigContext } from '../../../../ui-config/context/useConfigContext'
import { requestCompleteInvoiceById, useInvoiceById } from '../../../data'

type Props = {
  visible: boolean
  setVisible: (params: boolean) => void
  invoiceId: string
}

type FormValues = {
  confirmationReasonCode?: string
  confirmationReasonDescription?: string
}
type FormFieldName = keyof FormValues

export const RejectInvoice: React.FC<Props> = ({
  visible,
  setVisible,
  invoiceId,
}) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uiConfig: config } = useConfigContext()

  const { data: invoice } = useInvoiceById(invoiceId)

  const { showToast } = useToastContext()
  const [isRequestRunning, setIsRequestRunning] = useState<boolean>(false)

  // ************************************************************
  // formik
  // ************************************************************
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      confirmationReasonCode: undefined,
      confirmationReasonDescription: '',
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'confirmationReasonCode',
          config: config.invoice.rejectInvoice.confirmationReasonCode,
        },
        {
          fieldName: 'confirmationReasonDescription',
          config: config.invoice.rejectInvoice.confirmationReasonDescription,
        },
      ]

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async formData => {
      // deactivate the buttons
      setIsRequestRunning(true)

      try {
        // close the dialog and reset
        handleClose()

        // patch request for the despatchAdvice
        await submitForm(formData)

        // navigate back to the start page
        navigate('/')

        // show success toast
        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t(
            'invoice.title.reject.dialog.reject.messages.success.summary',
            {
              orderId: invoice?.Payload.OrderId,
            },
          ),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
      }
    },
  })

  const isFormFieldValid = (name: FormFieldName) =>
    !(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return (
      !isFormFieldValid(name) && (
        <small className="p-error">{formik.errors[name]}</small>
      )
    )
  }

  const handleClose = () => {
    setVisible(false) // hide the dialog
    formik.resetForm()
  }

  const submitForm = async (formData: FormValues) => {
    // make a copy of invoice
    const newInvoice = produce(invoice!, draft => {
      draft.Payload.ConfirmationCode = 'REJECTED'
      draft.Payload.ConfirmationDate = new Date().toISOString()

      const confirmationReason = {
        Code: formData.confirmationReasonCode,
        Description: formData.confirmationReasonDescription,
      }
      draft.Payload.ConfirmationReason = confirmationReason
    })

    await requestCompleteInvoiceById(
      invoiceId,
      prepareCompletePatchDto(newInvoice, invoice!),
    )
  }

  // ************************************************************
  // components
  // ************************************************************
  // confirmationReasonCode
  const confirmationReasonCodeLabel = (
    <label
      htmlFor="confirmationReasonCode"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonCode'),
      })}
    ></label>
  )

  useSelectFirstAndOnlyOption(
    'confirmationReasonCode',
    config.invoice.rejectInvoice.confirmationReasonCode.options,
    formik,
  )

  const confirmationReasonCodeComponent = (
    <Dropdown
      id="confirmationReasonCode"
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonCode'),
      })}
      value={formik.values.confirmationReasonCode}
      onChange={formik.handleChange}
      optionLabel="code"
      optionValue="code"
    />
  )

  const confirmationReasonCodeErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonCode')}</div>
  )

  const confirmationReasonCodeWrapper = (
    <ConfigurableComponent
      config={config.invoice.rejectInvoice}
      component={confirmationReasonCodeComponent}
      label={confirmationReasonCodeLabel}
      errorMsg={confirmationReasonCodeErrorMsg}
    />
  )

  // confirmationReasonDescription
  const confirmationReasonDescriptionLabel = (
    <label
      htmlFor="confirmationReasonDescription"
      className={classNames('text-sm', {
        'p-error': !isFormFieldValid('confirmationReasonDescription'),
      })}
    ></label>
  )

  const confirmationReasonDescriptionComponent = (
    <InputText
      id="confirmationReasonDescription"
      value={formik.values.confirmationReasonDescription}
      onChange={formik.handleChange}
      className={classNames('w-full', {
        'p-invalid': !isFormFieldValid('confirmationReasonDescription'),
      })}
    />
  )

  const confirmationReasonDescriptionErrorMsg = (
    <div>{getFormErrorMessage('confirmationReasonDescription')}</div>
  )

  const confirmationReasonDescriptionWrapper = (
    <ConfigurableComponent
      config={config.invoice.rejectInvoice}
      component={confirmationReasonDescriptionComponent}
      label={confirmationReasonDescriptionLabel}
      errorMsg={confirmationReasonDescriptionErrorMsg}
    />
  )

  // ************************************************************
  // dialog elements
  // ************************************************************
  // header
  const header = <>{t(`invoice.title.reject.dialog.title`)}</>

  // footer
  const footer = (
    <>
      <Button
        label={t(`common.buttons.cancel`)}
        icon="pi pi-times"
        className="p-button-text mt-3 w-full md:w-auto mb-1 md:mb-0"
        onClick={handleClose}
      />
      <Button
        label={t(`invoice.title.reject.dialog.reject.label`)}
        icon={'pi pi-times-circle'}
        className="w-full md:w-auto"
        onClick={formik.submitForm}
        disabled={!formik.dirty}
        loading={isRequestRunning}
      />
    </>
  )

  // object buyer to minify the code in the return
  const buyer = {
    company: invoice?.Payload.BuyerInformation.Company,
    name: invoice?.Payload.BuyerInformation.Contact?.Name,
    adress: {
      street: invoice?.Payload.BuyerInformation.Address?.Street,
      number: invoice?.Payload.BuyerInformation.Address?.Number,
      postalCode: invoice?.Payload.BuyerInformation.Address?.PostalCode,
      city: invoice?.Payload.BuyerInformation.Address?.City,
      countryCode: invoice?.Payload.BuyerInformation.Address?.CountryCode,
    },
    email: invoice?.Payload.BuyerInformation.Contact?.Email,
    phone: invoice?.Payload.BuyerInformation.Contact?.Phone,
  }

  return (
    <>
      <Dialog
        visible={visible}
        header={header}
        style={{ width: '30rem' }}
        breakpoints={{ [calculateBreakpointWidth(30)]: '90vw' }}
        footer={footer}
        onHide={handleClose}
        blockScroll
      >
        <div className="formgrid grid">
          <div className="col-12">
            <h3>{t(`common.headings.contact`)}</h3>
            <p className="my-2">{buyer.company}</p>
            <p className="my-2">{buyer.name}</p>
            <p className="my-2">
              {buyer.adress.street} {buyer.adress.number}
              <br />
              {buyer.adress.postalCode} {buyer.adress.city}
              <br />
              {buyer.adress.street} {buyer.adress.countryCode}
            </p>

            {buyer.email && (
              <p className="my-2">
                <a
                  className="no-underline"
                  href={`mailto:${buyer.email}?subject=${t(
                    'invoice.title.title',
                    {
                      orderId: invoice?.Payload.OrderId,
                    },
                  )}`}
                >
                  <i className="pi pi-envelope mr-2"></i>
                  {buyer.email}
                </a>
              </p>
            )}

            {buyer.phone && (
              <p className="my-2">
                <a className="no-underline" href={`tel:${buyer.phone}`}>
                  <i className="pi pi-phone mr-2"></i>
                  {buyer.phone}
                </a>
              </p>
            )}
          </div>
          <div className="col-12">{confirmationReasonCodeWrapper}</div>

          <div className="col-12">{confirmationReasonDescriptionWrapper}</div>
        </div>
      </Dialog>
    </>
  )
}
